import { externalLinkProps } from '@iheartjane/menu';
import type { Nullable, Nullish } from '@iheartjane/types';

import { useMainSiteUrl } from '@jane/bloom/hooks/useMainSiteUrl';
import type { ImageBannerViewModel } from '@jane/bloom/viewModels/communicationBanner';

import styles from './imageBanner.module.css';

type ImageBannerProps = {
  communicationBanner: Nullable<ImageBannerViewModel>;
};

/**
 * Operators author `button_link` by hand and often omit the scheme, so a bare
 * `dispensary.com` would otherwise resolve as a path on the current menu.
 */
const withScheme = (url: string) =>
  /^https?:\/\//i.test(url) ? url : `http://${url}`;

type CtaLinkProps = {
  comparisonUrl: string;
  destination: string;
  fillColor: Nullish<string>;
  label: Nullish<string>;
  textColor: Nullish<string>;
};

const CtaLink = ({
  comparisonUrl,
  destination,
  fillColor,
  label,
  textColor,
}: CtaLinkProps) => {
  // href stays a literal attribute rather than part of the spread so jsx-a11y
  // can still see this as an interactive element.
  const { href, ...newTabProps } = externalLinkProps(
    destination,
    comparisonUrl
  );

  return (
    <a
      className={styles['cta']}
      href={href}
      {...newTabProps}
      style={{
        backgroundColor: `#${fillColor}`,
        color: `#${textColor}`,
      }}
    >
      {label}
    </a>
  );
};

export const ImageBanner = ({ communicationBanner }: ImageBannerProps) => {
  const mainSiteUrl = useMainSiteUrl();

  if (!communicationBanner) return null;

  return (
    <div className={styles['container']}>
      <img
        title="Communication banner background image"
        alt=""
        style={{
          opacity: communicationBanner.image_opacity
            ? communicationBanner.image_opacity / 100
            : 1,
        }}
        src={communicationBanner.fallbackSrc}
        srcSet={communicationBanner.srcSet}
        sizes={communicationBanner.sizes}
        className={styles['backgroundImage']}
        loading="eager"
        height="240px"
        width="100%"
        decoding="auto"
      />
      <h1
        style={{
          color: `#${communicationBanner.header_text_color}`,
        }}
      >
        {communicationBanner.title}
      </h1>
      {communicationBanner.button_link && (
        <CtaLink
          destination={withScheme(communicationBanner.button_link)}
          comparisonUrl={mainSiteUrl}
          fillColor={communicationBanner.button_fill_color}
          label={communicationBanner.button_text}
          textColor={communicationBanner.button_text_color}
        />
      )}
    </div>
  );
};
